import type { CSSProperties, ReactNode } from "react";

interface RingProps {
  /** 0..1 */
  pct: number;
  size: number;
  stroke: number;
  color?: string;
  track?: string;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * v2 (SNIPPETS §17): the figure in the middle, in the figure face at the kit's size for the
   * ring — 20 at 72, 30 at 120, 76 on a 248 player ring. Used instead of `children`.
   */
  figure?: ReactNode;
  /** a player ring: figure in --on-surface; pass track="var(--on-surface-track)" with it */
  onSurface?: boolean;
}

function figureSize(size: number): string {
  if (size >= 200) return "var(--fs-player)";
  if (size >= 100) return "1.875rem";
  return "1.25rem";
}

/** The progress ring used everywhere: week strip, hero, player countdown and treadmill. */
export function Ring({
  pct,
  size,
  stroke,
  color = "var(--pine)",
  track = "var(--track)",
  children,
  className,
  style,
  figure,
  onSurface,
}: RingProps) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(1, pct)));

  const svg = (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
      <circle
        className="arc"
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c.toFixed(1)}
        strokeDashoffset={offset.toFixed(1)}
        style={{ transition: "stroke-dashoffset .3s" }}
      />
    </svg>
  );

  if (figure !== undefined && figure !== null) {
    return (
      <div
        className={`ringwrap k-ring${onSurface ? " k-ring--on-surface" : ""} ${className || ""}`}
        style={{ width: size, height: size, ...style }}
      >
        {svg}
        <div className="c">
          <span className="k-ring__fig" style={{ fontSize: figureSize(size) }}>
            {figure}
          </span>
        </div>
      </div>
    );
  }

  if (!children) return svg;

  return (
    <div className={`ringwrap ${className || ""}`} style={{ width: size, height: size, ...style }}>
      {svg}
      <div className="c">{children}</div>
    </div>
  );
}
